import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { fetchPlans, type BillingPlanCode, type Plan } from '@/api/billing'
import { openRazorpayCheckout } from '@/lib/openRazorpayCheckout'
import { discountPercent } from '@/lib/planPricing'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { Button } from '@/components/ui/button'

function formatInr(paise: number): string {
  return `₹${(paise / 100).toLocaleString('en-IN')}`
}

const BLURB: Record<BillingPlanCode, string> = {
  MONTHLY: 'Billed every month. Cancel anytime.',
  ANNUAL: 'Billed once a year — the cheapest way to stay subscribed.',
}

function planPriceSuffix(plan: Plan): string {
  return plan.interval === 'MONTH' ? '/mo' : '/yr'
}

export function PlanPicker({
  canPurchase,
  disabledReason,
  onPurchased,
}: {
  canPurchase: boolean
  disabledReason?: string
  onPurchased?: () => void
}) {
  const { data: user } = useCurrentUser()
  const queryClient = useQueryClient()
  const { data: plans, isPending } = useQuery({ queryKey: ['billing-plans'], queryFn: fetchPlans })
  const [busy, setBusy] = useState<BillingPlanCode | null>(null)

  async function choose(code: BillingPlanCode) {
    setBusy(code)
    try {
      const paid = await openRazorpayCheckout(code, {
        name: user?.displayName ?? undefined,
        email: user?.email,
      })
      if (paid) {
        toast.success('Payment confirmed — thanks for subscribing!')
        await queryClient.invalidateQueries({ queryKey: ['entitlement'] })
        await queryClient.invalidateQueries({ queryKey: ['billing-status'] })
        onPurchased?.()
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Checkout failed. Please try again.')
    } finally {
      setBusy(null)
    }
  }

  if (isPending) return <p className="text-sm text-[var(--fl-text-3)]">Loading plans…</p>

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {(plans ?? []).map((plan) => {
        const suffix = planPriceSuffix(plan)
        const off = discountPercent(plan.amountPaise, plan.listAmountPaise)
        return (
          <div
            key={plan.code}
            className="flex flex-col rounded-xl border border-[var(--fl-border)] bg-[var(--fl-surface)] p-5"
          >
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-bold uppercase tracking-wide text-[var(--fl-heading)]">
                {plan.code.toLowerCase()}
              </h3>
              {off > 0 && (
                <span className="rounded-full bg-[var(--fl-primary-subtle)] px-2 py-0.5 text-[11px] font-semibold text-[var(--fl-primary-text)]">
                  {off}% off
                </span>
              )}
            </div>
            {off > 0 && (
              <p className="mt-2 text-sm font-medium text-[var(--fl-text-3)] line-through">
                {formatInr(plan.listAmountPaise)}
                {suffix}
              </p>
            )}
            <p
              className={`${off > 0 ? 'mt-0.5' : 'mt-2'} font-serif text-3xl font-bold text-[var(--fl-heading)]`}
            >
              {formatInr(plan.amountPaise)}
              <span className="text-base font-medium text-[var(--fl-text-3)]">{suffix}</span>
            </p>
            <p className="mt-2 flex-1 text-sm text-[var(--fl-text-2)]">{BLURB[plan.code]}</p>
            <Button
              type="button"
              onClick={() => choose(plan.code)}
              disabled={!canPurchase || busy !== null}
              className="mt-4 w-full"
            >
              {busy === plan.code ? 'Opening…' : `Choose ${plan.code.toLowerCase()}`}
            </Button>
          </div>
        )
      })}
      {!canPurchase && disabledReason && (
        <p className="sm:col-span-2 text-sm text-[var(--fl-text-2)]">{disabledReason}</p>
      )}
    </div>
  )
}
